import { Component } from '@theme/component';
import { StandardEvents, CartErrorEvent } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';
import { mutateCart, cartSectionIds, fetchCart } from '@swati/cart';
import { fetchConfig } from '@theme/utilities';

/**
 * @typedef {Object} MixMatchRefs
 * @property {HTMLElement} progressContainer
 * @property {HTMLElement} progressBar
 * @property {HTMLElement} progressFill
 * @property {HTMLElement} progressSteps
 * @property {HTMLElement} progressMessage
 * @property {HTMLElement} progressHeadline
 * @property {HTMLElement} mobileProgressContainer
 * @property {HTMLElement} mobileProgressBar
 * @property {HTMLElement} mobileProgressFill
 * @property {HTMLElement} mobileProgressSteps
 * @property {HTMLElement} mobileProgressMessage
 * @property {HTMLElement} progressContent
 * @property {HTMLElement} mobileSubtotal
 * @property {HTMLElement} bundlePanel
 * @property {HTMLElement} mobileSummaryToggle
 * @property {HTMLElement} mobileDrawerCloseButton
 * @property {HTMLElement} cartFeedback
 */
class MixMatchCollectionComponent extends Component {
  /** @type {number | undefined} */
  #errorTimeout;
  /** @type {Array<{quantity: number, value: number}> | null} */
  #cachedTiers = null;
  /** @type {string} */
  #defaultProgressHeadline = '';
  /** @type {boolean} */
  #isSyncingEyePower = false;
  /** @type {string} */
  #sharedEyePowerValue = '';
  /** @type {boolean} */
  #isMobileDrawerOpen = false;
  /** @type {MutationObserver | undefined} */
  #drawerAttrObserver;
  /** @type {number} */
  #lastScrollY = 0;
  /** @type {boolean} */
  #isCartBusy = false;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #shakeInterval;
  /**
   * The `auto-open` value the cart drawer carried before this component suppressed it,
   * or `null` when it carried none.
   * @type {string | null}
   */
  #cartDrawerAutoOpenValue = null;

  connectedCallback() {
    super.connectedCallback();

    this.dataset.mobileDrawerOpen = 'false';
    this.#setMobileDrawerState(false, { manageFocus: false });

    this.#drawerAttrObserver = new MutationObserver(() => {
      if (this.#isMobileDrawerOpen && this.dataset.mobileDrawerOpen !== 'true') {
        this.dataset.mobileDrawerOpen = 'true';
      }
    });
    this.#drawerAttrObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-mobile-drawer-open'],
    });
    this.#defaultProgressHeadline = this.refs.progressHeadline?.textContent?.trim() || '';

    this.#markCardsAsNoNavigation();
    this.#normalizeCardCTAs();
    this.#initializeEyePowerControls();
    this.#forceDefaultGridView();

    this.#initProgress();
    this.#renderFallbackComponents();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(StandardEvents.cartError, this.#handleCartError);
    this.#suppressCartDrawerAutoOpen();

    this.addEventListener('click', this.#handleCardAnchorClick, true);
    this.addEventListener('click', this.#handleBundleComponentAction);
    this.addEventListener('change', this.#handleEyePowerChange);
    this.addEventListener('keydown', this.#handleEscapeToCloseDrawer);

    // scroll-ok: the mobile summary only exists below 990px, where the document itself scrolls
    this.#lastScrollY = window.scrollY;
    window.addEventListener('scroll', this.#handleMobileSummaryScroll, { passive: true });

    const mobileSummary = this.querySelector('.mix-match-mobile-summary');
    if (mobileSummary instanceof HTMLElement) {
      mobileSummary.classList.add('is-scroll-hidden');
    }

    this.#initShakeAnimation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#errorTimeout) {
      clearTimeout(this.#errorTimeout);
    }

    if (this.#shakeInterval) {
      clearInterval(this.#shakeInterval);
    }

    this.#drawerAttrObserver?.disconnect();
    if (this.#isMobileDrawerOpen) {
      document.body.style.overflow = '';
    }
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(StandardEvents.cartError, this.#handleCartError);
    this.#restoreCartDrawerAutoOpen();

    this.removeEventListener('click', this.#handleCardAnchorClick, true);
    this.removeEventListener('click', this.#handleBundleComponentAction);
    this.removeEventListener('change', this.#handleEyePowerChange);
    this.removeEventListener('keydown', this.#handleEscapeToCloseDrawer);
    window.removeEventListener('scroll', this.#handleMobileSummaryScroll);
  }

  /**
   * Stop the cart drawer auto-opening while Mix & Match owns the page.
   *
   * `<cart-drawer-component>` decides `shouldAutoOpen` synchronously, at dispatch time, from
   * its own `auto-open` attribute — there is no per-event opt out to pass any more — so the
   * attribute is taken away instead. The capture-phase listener on `document` always runs
   * before the drawer's own bubble-phase listener on `document`, so the suppression survives
   * a cart morph that puts the attribute back.
   */
  #suppressCartDrawerAutoOpen() {
    this.#cartDrawerAutoOpenValue = document.querySelector('cart-drawer-component')?.getAttribute('auto-open') ?? null;

    this.#stripCartDrawerAutoOpen();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#stripCartDrawerAutoOpen, { capture: true });
  }

  /**
   * Hand the cart drawer its `auto-open` attribute back, if it had one.
   */
  #restoreCartDrawerAutoOpen() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#stripCartDrawerAutoOpen, { capture: true });

    if (this.#cartDrawerAutoOpenValue === null) return;

    document.querySelector('cart-drawer-component')?.setAttribute('auto-open', this.#cartDrawerAutoOpenValue);
  }

  #stripCartDrawerAutoOpen = () => {
    document.querySelector('cart-drawer-component')?.removeAttribute('auto-open');
  };

  /**
   * Handle colour tab click by updating the current filter query and reusing facets rendering.
   * @param {{ param?: string, value?: string }} data
   * @param {Event} event
   */
  onColourTabClick = (data, event) => {
    event.preventDefault();

    const param = data?.param;
    if (!param) return;

    const url = new URL(window.location.href);

    url.searchParams.delete(param);
    url.searchParams.delete('page');

    const value = typeof data.value === 'string' ? data.value : '';
    if (value) {
      url.searchParams.append(param, value);
    }

    const facetsForm = this.querySelector('facets-form-component');

    if (facetsForm && typeof facetsForm.updateFiltersByURL === 'function') {
      facetsForm.updateFiltersByURL(url.toString());
      return;
    }

    window.location.assign(url.toString());
  };

  /**
   * Handle colour dropdown change on mobile.
   * @param {Object} _data
   * @param {Event} event
   */
  onColourSelectChange = (_data, event) => {
    if (!event) event = _data;
    if (!(event.target instanceof HTMLSelectElement)) return;

    const filterWrapper = event.target.closest('[data-colour-param]');
    const param = filterWrapper?.dataset?.colourParam;
    if (!param) return;

    const value = event.target.value;
    const url = new URL(window.location.href);

    url.searchParams.delete(param);
    url.searchParams.delete('page');

    if (value) {
      url.searchParams.append(param, value);
    }

    const facetsForm = this.querySelector('facets-form-component');

    if (facetsForm && typeof facetsForm.updateFiltersByURL === 'function') {
      facetsForm.updateFiltersByURL(url.toString());
      return;
    }

    window.location.assign(url.toString());
  };

  /**
   * Add selected eye-power variant to the bundle parent line item.
   * Falls back to legacy individual-add behavior when no bundle parent is configured.
   * @param {Event} event
   */
  openQuickAdd = async (event) => {
    event.preventDefault();

    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-mix-match-open-quick-add]') || event.target.closest('button');
    if (!(trigger instanceof HTMLButtonElement)) return;

    const productCard = trigger.closest('product-card') || event.target.closest('product-card');
    if (!(productCard instanceof HTMLElement)) return;

    const eyePowerSelect = productCard.querySelector('[data-mix-match-eye-select]');
    if (!(eyePowerSelect instanceof HTMLSelectElement)) return;

    const selectedOption = eyePowerSelect.options[eyePowerSelect.selectedIndex];
    const variantId = selectedOption?.dataset.variantId;
    const isAvailable = selectedOption?.dataset.available !== 'false';

    if (!variantId) {
      this.#dispatchCartError(this.dataset.selectEyePowerMessage || eyePowerSelect.labels?.[0]?.textContent || '');
      return;
    }

    if (!isAvailable) {
      this.#dispatchCartError(this.dataset.soldOutLabel || selectedOption.textContent?.trim() || '');
      return;
    }

    if (this.#isCartBusy || trigger.dataset.mixMatchBusy === 'true') return;
    this.#isCartBusy = true;
    trigger.dataset.mixMatchBusy = 'true';
    trigger.disabled = true;
    let didSucceed = false;

    const bundleParentVariantId = this.dataset.bundleParentVariantId;

    const sectionsParam = this.#collectSectionIds();

    try {
      // The standard cart event is one event per *mutation*, so the branch has to be settled
      // before anything is dispatched. Only the bundle-parent branches need the cart, so the
      // legacy path still makes no extra `/cart.js` request.
      const existingLine = bundleParentVariantId ? this.#findExistingBundleLine(await this.#getCart()) : undefined;

      /** @type {{ action: 'add' | 'update', lines: Array<Record<string, any>>, url: string, body: string }} */
      let mutation;

      if (!bundleParentVariantId) {
        mutation = {
          action: 'add',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          url: Theme.routes.cart_add_url,
          body: JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity: 1,
                properties: {
                  _bundle_type: 'mix-match',
                },
              },
            ],
            sections: sectionsParam,
            sections_url: window.location.pathname + window.location.search,
          }),
        };
      } else if (!existingLine) {
        const components = [{ variantId: Number(variantId), qty: 1, productId: Number(productCard.dataset.productId) }];

        mutation = {
          action: 'add',
          lines: [{ merchandiseId: String(bundleParentVariantId), quantity: 1 }],
          url: Theme.routes.cart_add_url,
          body: JSON.stringify({
            items: [
              {
                id: Number(bundleParentVariantId),
                quantity: 1,
                properties: {
                  _bundle_type: 'mix-match',
                  _components: JSON.stringify(components),
                },
              },
            ],
            sections: sectionsParam,
            sections_url: window.location.pathname + window.location.search,
          }),
        };
      } else {
        const components = this.#parseComponents(existingLine);
        this.#addOrIncrementComponent(components, Number(variantId), Number(productCard.dataset.productId));

        mutation = {
          action: 'update',
          lines: [{ id: existingLine.key, quantity: 1 }],
          url: Theme.routes.cart_change_url,
          body: JSON.stringify({
            id: existingLine.key,
            quantity: 1,
            properties: {
              ...existingLine.properties,
              _bundle_type: 'mix-match',
              _components: JSON.stringify(components),
            },
            sections: sectionsParam,
            sections_url: window.location.pathname + window.location.search,
          }),
        };
      }

      const result = await mutateCart({
        source: this,
        action: mutation.action,
        context: 'product',
        lines: mutation.lines,
        detail: {
          source: 'mix-match-collection-component',
          sourceId: this.id || undefined,
          productId: productCard.dataset.productId,
          itemCount: 1,
        },
        run: async () => {
          const response = await fetch(mutation.url, fetchConfig('json', { body: mutation.body }));
          const responseText = await response.text();
          return responseText ? JSON.parse(responseText) : {};
        },
      });

      if (!result.ok) {
        // `mutateCart` already dispatched a `CartErrorEvent` carrying Shopify's own message;
        // a second, generic one would only overwrite it.
        if (!result.errorDispatched) {
          this.#dispatchCartError(this.dataset.cartUpdateError || 'Unable to update cart');
        }
        return;
      }

      didSucceed = true;
    } catch (error) {
      this.#dispatchCartError(this.dataset.cartUpdateError || 'Unable to update cart');
      console.error('Failed to add mix-match item to cart:', error);
    } finally {
      this.#isCartBusy = false;
      delete trigger.dataset.mixMatchBusy;
      this.#updateCardActionState(productCard);
      if (didSucceed) {
        this.#flashAddedState(trigger);
      }
    }
  };

  /**
   * Toggle local mobile bundle drawer.
   * @param {Event} event
   */
  toggleMobileBundleDrawer = (event) => {
    event.preventDefault();

    if (event.target instanceof Element && event.target.closest('[data-mix-match-checkout]')) {
      return;
    }

    const shouldOpen = this.dataset.mobileDrawerOpen !== 'true';
    this.#setMobileDrawerState(shouldOpen);
  };

  closeMobileBundleDrawer = (event) => {
    event.preventDefault();
    this.#setMobileDrawerState(false);
  };

  goToCheckout = (event) => {
  event.preventDefault();
  const btn = event.target.closest('[data-mix-match-checkout]');
  if (btn?.hasAttribute('aria-disabled')) return;
  const url = btn?.dataset.checkoutUrl || '/checkout';
  window.location.href = url;
};

  /**
   * Reveal the floating mobile summary bar when the peek strip is tapped.
   */
  revealMobileSummary = () => {
    const mobileSummary = this.querySelector('.mix-match-mobile-summary');
    if (mobileSummary instanceof HTMLElement) {
      mobileSummary.classList.remove('is-scroll-hidden');
    }
  };

  #handleEscapeToCloseDrawer = (event) => {
    if (!(event instanceof KeyboardEvent)) return;
    if (event.key !== 'Escape') return;
    if (this.dataset.mobileDrawerOpen !== 'true') return;

    this.#setMobileDrawerState(false);
  };

  #handleMobileSummaryScroll = () => {
    if (this.#isMobileDrawerOpen) return;

    const mobileSummary = this.querySelector('.mix-match-mobile-summary');
    if (!(mobileSummary instanceof HTMLElement)) return;

    // scroll-ok: the mobile summary only exists below 990px, where the document itself scrolls
    const currentY = window.scrollY;
    const delta = currentY - this.#lastScrollY;

    if (Math.abs(delta) < 8) return;

    if (delta > 0) {
      mobileSummary.classList.add('is-scroll-hidden');
    } else {
      mobileSummary.classList.remove('is-scroll-hidden');
    }

    this.#lastScrollY = currentY;
  };

  /**
   * Initialize shake animation for empty slots, respecting prefers-reduced-motion.
   */
  #initShakeAnimation() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const triggerShake = () => {
      const slots = this.querySelectorAll('.mix-match-slot--shake');
      slots.forEach((slot) => {
        slot.classList.remove('is-shaking');
        void slot.offsetWidth;
        slot.classList.add('is-shaking');
      });
    };

    setTimeout(triggerShake, 2000);
    this.#shakeInterval = setInterval(triggerShake, 6000);
  }

  /**
   * Prevent product card anchor navigation in mix-match mode.
   * @param {Event} event
   */
  #handleCardAnchorClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const addToBundleTrigger = event.target.closest('[data-mix-match-open-quick-add]');
    if (addToBundleTrigger instanceof Element) {
      this.openQuickAdd(event);
      event.stopPropagation();
      return;
    }

    if (event.target.closest('button, input, select, label, quantity-selector-component, quick-add-component')) {
      return;
    }

    const productCard = event.target.closest('product-card');
    if (!productCard) return;

    event.preventDefault();

    const addButton = productCard.querySelector('[data-mix-match-open-quick-add]');
    if (addButton instanceof HTMLElement) {
      addButton.focus();
    }
  };

  /**
   * Keep all eye-power selectors in sync across cards.
   * @param {Event} event
   */
  #handleEyePowerChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    if (!event.target.matches('[data-mix-match-eye-select]')) return;
    if (this.#isSyncingEyePower) return;

    this.#sharedEyePowerValue = event.target.value;
    this.#syncEyePowerAcrossCards(this.#sharedEyePowerValue);

    try { localStorage.setItem('p', JSON.stringify(this.#sharedEyePowerValue)); } catch (_e) {}
  };

  #setMobileDrawerState(isOpen, options = {}) {
    const shouldManageFocus = options.manageFocus !== false;
    this.#isMobileDrawerOpen = isOpen;
    this.dataset.mobileDrawerOpen = String(isOpen);

    const mobileSummary = this.querySelector('.mix-match-mobile-summary');
    if (mobileSummary instanceof HTMLElement) {
      mobileSummary.classList.remove('is-scroll-hidden');
    }

    const bundlePanel = this.refs.bundlePanel;
    const toggle = this.refs.mobileSummaryToggle;
    const isMobileViewport = window.matchMedia('(max-width: 989px)').matches;

    if (bundlePanel instanceof HTMLElement) {
      bundlePanel.setAttribute('aria-hidden', String(isMobileViewport ? !isOpen : false));
    }

    if (toggle instanceof HTMLElement) {
      toggle.setAttribute('aria-expanded', String(isOpen));
    }

    if (isMobileViewport) {
      document.body.style.overflow = isOpen ? 'hidden' : '';
    }

    if (isOpen) {
      if (shouldManageFocus) {
        this.refs.mobileDrawerCloseButton?.focus();
      }
      return;
    }

    if (shouldManageFocus) {
      toggle?.focus();
    }
  }

  #markCardsAsNoNavigation() {
    const productCards = this.querySelectorAll('product-card');
    for (const productCard of productCards) {
      productCard.setAttribute('data-no-navigation', 'true');
    }

    const cardLinks = this.querySelectorAll('product-card-link');
    for (const link of cardLinks) {
      link.setAttribute('aria-disabled', 'true');
      link.setAttribute('tabindex', '-1');
    }

    const cardAnchors = this.querySelectorAll('.product-card__link');
    for (const anchor of cardAnchors) {
      if (anchor instanceof HTMLElement) {
        anchor.setAttribute('tabindex', '-1');
        anchor.setAttribute('aria-hidden', 'true');
      }
    }
  }

  #normalizeCardCTAs() {
    const ctaLabel = this.dataset.addToBundleLabel?.trim() || 'Add to bundle';
    const productCards = this.querySelectorAll('product-card');

    for (const productCard of productCards) {
      const content = productCard.querySelector('.product-card__content');
      if (!(content instanceof HTMLElement)) continue;

      const mixMatchButton = content.querySelector('[data-mix-match-open-quick-add]');
      if (mixMatchButton instanceof HTMLButtonElement) {
        if (!mixMatchButton.querySelector('.add-to-cart-text')) {
          mixMatchButton.innerHTML =
            `<span class="add-to-cart-text"><span>${this.#escapeHtml(ctaLabel)}</span></span>` +
            `<span class="add-to-cart-text--added" aria-hidden="true">` +
              `<span class="svg-wrapper add-to-cart-icon--added">${MixMatchCollectionComponent.#checkmarkSvg}</span>` +
            `</span>`;
        }
        mixMatchButton.dataset.mixMatchOpenQuickAdd = 'true';
        this.#updateCardActionState(productCard);
        continue;
      }

      if (mixMatchButton instanceof HTMLAnchorElement) {
        const replacement = this.#buildAddToBundleButton(ctaLabel, mixMatchButton.className, mixMatchButton.getAttribute('style'));
        mixMatchButton.replaceWith(replacement);
        this.#updateCardActionState(productCard);
        continue;
      }

      const directButtonLink = content.querySelector(':scope > a');
      if (directButtonLink instanceof HTMLAnchorElement) {
        const replacement = this.#buildAddToBundleButton(
          ctaLabel,
          directButtonLink.className,
          directButtonLink.getAttribute('style')
        );
        directButtonLink.replaceWith(replacement);
        this.#updateCardActionState(productCard);
        continue;
      }

      const fallbackLink = content.querySelector('a[href*="/products/"]');
      if (fallbackLink instanceof HTMLAnchorElement) {
        const replacement = this.#buildAddToBundleButton(ctaLabel, fallbackLink.className, fallbackLink.getAttribute('style'));
        fallbackLink.replaceWith(replacement);
        this.#updateCardActionState(productCard);
        continue;
      }

      const fallbackButton = this.#buildAddToBundleButton(ctaLabel, 'button');
      content.appendChild(fallbackButton);
      this.#updateCardActionState(productCard);
    }
  }

  #buildAddToBundleButton(label, className, styleText = null) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className || 'button';
    button.dataset.mixMatchOpenQuickAdd = 'true';

    button.innerHTML =
      `<span class="add-to-cart-text"><span>${this.#escapeHtml(label)}</span></span>` +
      `<span class="add-to-cart-text--added" aria-hidden="true">` +
        `<span class="svg-wrapper add-to-cart-icon--added">${MixMatchCollectionComponent.#checkmarkSvg}</span>` +
      `</span>`;

    if (styleText) {
      button.setAttribute('style', styleText);
    }

    return button;
  }

  static #checkmarkSvg = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-checkmark"><path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  #initializeEyePowerControls() {
    const eyePowerSelects = this.querySelectorAll('[data-mix-match-eye-select]');
    if (eyePowerSelects.length === 0) return;

    try {
      const stored = localStorage.getItem('p');
      if (stored) {
        let value = '';
        try { value = JSON.parse(stored); } catch { value = stored; }
        if (typeof value === 'string' && value.includes(',')) {
          value = value.split(',')[0];
        }
        if (typeof value === 'string' && value.trim() !== '') {
          this.#sharedEyePowerValue = value;
        }
      }
    } catch (_e) {}

    if (!this.#sharedEyePowerValue) {
      const firstSelect = eyePowerSelects[0];
      if (firstSelect instanceof HTMLSelectElement) {
        this.#sharedEyePowerValue = firstSelect.value;
      }
    }

    if (this.#sharedEyePowerValue) {
      this.#syncEyePowerAcrossCards(this.#sharedEyePowerValue);
      return;
    }

    for (const eyePowerSelect of eyePowerSelects) {
      if (!(eyePowerSelect instanceof HTMLSelectElement)) continue;
      const card = eyePowerSelect.closest('product-card');
      this.#updateCardActionState(card);
    }
  }

  #syncEyePowerAcrossCards(value) {
    const eyePowerSelects = this.querySelectorAll('[data-mix-match-eye-select]');

    this.#isSyncingEyePower = true;
    try {
      for (const eyePowerSelect of eyePowerSelects) {
        if (!(eyePowerSelect instanceof HTMLSelectElement)) continue;

        const matchingOption = Array.from(eyePowerSelect.options).find((option) => option.value === value);
        if (matchingOption) {
          eyePowerSelect.value = value;
        }

        const card = eyePowerSelect.closest('product-card');
        this.#updateCardActionState(card);
      }
    } finally {
      this.#isSyncingEyePower = false;
    }
  }

  #updateCardActionState(card) {
    if (!(card instanceof Element)) return;

    const addButton = card.querySelector('[data-mix-match-open-quick-add]');
    const eyePowerSelect = card.querySelector('[data-mix-match-eye-select]');
    if (!(addButton instanceof HTMLButtonElement)) return;

    if (!(eyePowerSelect instanceof HTMLSelectElement)) {
      addButton.disabled = addButton.dataset.mixMatchBusy === 'true';
      return;
    }

    const selectedOption = eyePowerSelect.options[eyePowerSelect.selectedIndex];
    const hasVariant = Boolean(selectedOption?.dataset.variantId);
    const isAvailable = selectedOption?.dataset.available !== 'false';
    const isBusy = addButton.dataset.mixMatchBusy === 'true';

    addButton.disabled = !hasVariant || !isAvailable || isBusy;
  }

  /**
   * Briefly show a checkmark "added" state on the button.
   * @param {HTMLButtonElement} button
   */
  #flashAddedState(button) {
    if (!(button instanceof HTMLElement)) return;
    button.classList.add('atc-added');
    setTimeout(() => {
      button.classList.remove('atc-added');
    }, 1500);
  }

  #dispatchCartError(message, description = '', errors = {}) {
    const fallbackMessage = this.dataset.cartUpdateError || 'Unable to update cart';
    const resolvedMessage = message && message.toString().trim() ? message : fallbackMessage;

    this.dispatchEvent(
      new CartErrorEvent({
        error: resolvedMessage,
        code: 'INVALID',
        detail: {
          description: description || resolvedMessage,
          errors,
          source: 'mix-match-collection-component',
        },
      })
    );
  }

  #forceDefaultGridView() {
    const productGrid = this.querySelector('.mix-match-products .product-grid');
    if (productGrid instanceof HTMLElement) {
      productGrid.setAttribute('product-grid-view', 'default');
    }

    const desktopGridInputs = this.querySelectorAll('input[type="radio"][name="grid"]');
    for (const input of desktopGridInputs) {
      if (input instanceof HTMLInputElement) {
        input.checked = input.value === 'default';
      }
    }

    try {
      sessionStorage.removeItem('product-grid-view-desktop');
      sessionStorage.removeItem('product-grid-view-mobile');
    } catch (error) {
      // Ignore session storage access issues in private browsing contexts.
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#restoreProgressAfterMorph();
    this.#renderFallbackComponents();
    this.#normalizeCardCTAs();
  }

  /**
   * `shopify:cart:lines-update` fires *before* the request and before any section morph, so
   * only the work that is correct straight away stays synchronous — everything that reads DOM
   * a morph replaces waits on the event's deferred promise.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    this.#clearError();

    const mobileSummary = this.querySelector('.mix-match-mobile-summary');
    if (mobileSummary instanceof HTMLElement) {
      mobileSummary.classList.remove('is-scroll-hidden');
    }

    event.promise
      ?.then(async () => {
        // Our own section is excluded from the `sections` request param, and `updatedCallback()`
        // never runs after a morph (`morph.js` hands `onAfterUpdate` the un-upgraded DOMParser
        // node, so `node instanceof Component` is always false), so Mix & Match refreshes itself.
        const didRender = await this.#refreshOwnSection();

        this.#restoreAfterCartRender();

        if (!didRender) {
          // The in-section `<cart-items-component>` starts its own un-awaited fallback render;
          // run once more so the restores also land after that morph.
          setTimeout(this.#restoreAfterCartRender, 400);
        }
      }, () => {})
      .catch(() => {});
  };

  /**
   * Re-render this component's own section so the restores below have the final DOM.
   *
   * @returns {Promise<boolean>} `true` when the section was explicitly re-rendered.
   */
  async #refreshOwnSection() {
    const sectionId = this.dataset.sectionId || this.getAttribute('section-id');

    if (sectionId && typeof sectionRenderer?.renderSection === 'function') {
      try {
        // The current URL carries the active filters, so the re-render keeps them.
        await sectionRenderer.renderSection(sectionId, { cache: false });
        return true;
      } catch (error) {
        console.error('Failed to re-render the mix-match section:', error);
      }
    }

    // No usable section id, or the render failed: wait a frame plus a task so the restores
    // run after whichever morph does land.
    await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
    return false;
  }

  /**
   * Idempotent post-morph restores: progress bars, the bundle components table and the shared
   * eye-power selection. Safe to run more than once for the same mutation.
   */
  #restoreAfterCartRender = () => {
    this.#restoreProgressAfterMorph();
    this.#renderFallbackComponents();
    if (this.#sharedEyePowerValue) {
      this.#syncEyePowerAcrossCards(this.#sharedEyePowerValue);
    }
  };

  #initProgress() {
    const tiers = this.#getBundleTiers();
    if (tiers.length === 0) {
      if (this.refs.progressContainer?.hidden) {
        this.#hideProgressCompletely();
      }
      return;
    }
    this.#updateProgress();
  }

  /**
   * `CartErrorEvent` flattens its payload onto the event itself — there is no `detail.data`.
   *
   * @param {import('@shopify/events').CartErrorEvent} event
   */
  #handleCartError = (event) => {
    const feedback = this.refs.cartFeedback;
    if (!(feedback instanceof HTMLElement)) return;

    const message = event.error ?? event.detail?.description;
    if (!message) return;

    feedback.textContent = message;
    feedback.classList.remove('hidden');

    if (this.#errorTimeout) {
      clearTimeout(this.#errorTimeout);
    }

    this.#errorTimeout = window.setTimeout(() => {
      this.#clearError();
    }, 9000);
  };

  #clearError() {
    const feedback = this.refs.cartFeedback;
    if (!(feedback instanceof HTMLElement)) return;

    feedback.classList.add('hidden');
    feedback.textContent = '';
  }

  #updateProgress() {
    const tiers = this.#getBundleTiers();
    if (tiers.length === 0) return;

    const quantity = this.#getScopedQuantity();
    const maxQuantity = tiers[tiers.length - 1]?.quantity || 1;
    const fillPercent = Math.max(0, Math.min(100, (quantity / maxQuantity) * 100));

    const markersHtml = this.#buildMarkersHtml(tiers, quantity, maxQuantity);
    const messageHtml = this.#buildMessageHtml(tiers, quantity);

    this.#applyProgressToRefs(
      { bar: this.refs.progressBar, steps: this.refs.progressSteps, message: this.refs.progressMessage, headline: this.refs.progressHeadline },
      fillPercent, markersHtml, messageHtml, tiers, quantity
    );
    this.#applyProgressToRefs(
      { bar: this.refs.mobileProgressBar, steps: this.refs.mobileProgressSteps, message: this.refs.mobileProgressMessage },
      fillPercent, markersHtml, messageHtml, tiers, quantity
    );

    this.#renderDiscountTiers(quantity);
  }

  #restoreProgressAfterMorph() {
    const tiers = this.#getBundleTiers();
    if (tiers.length === 0) {
      if (this.refs.progressContainer?.hidden) {
        this.#hideProgressCompletely();
      }
      return;
    }

    this.#updateProgress();
  }

  #hideProgressCompletely() {
    const progressContainer = this.refs.progressContainer;
    const mobileProgressContainer = this.refs.mobileProgressContainer;

    if (progressContainer instanceof HTMLElement) progressContainer.hidden = true;
    if (mobileProgressContainer instanceof HTMLElement) mobileProgressContainer.hidden = true;

    const discountTiersContainer = document.querySelector('[data-mix-match-discount-tiers]');
    if (discountTiersContainer instanceof HTMLElement) discountTiersContainer.hidden = true;
  }

  #applyProgressToRefs(refs, fillPercent, markersHtml, messageHtml) {
    if (refs.bar instanceof HTMLElement) {
      refs.bar.style.setProperty('--progress-fill', `${fillPercent}%`);
      refs.bar.setAttribute('aria-valuenow', String(Math.round(fillPercent)));
    }
    if (refs.steps instanceof HTMLElement) {
      refs.steps.innerHTML = markersHtml;
    }
    if (refs.message instanceof HTMLElement) {
      refs.message.innerHTML = messageHtml;
    }
  }

  #buildMarkersHtml(tiers, quantity, maxQuantity) {
    const nextTier = tiers.find((tier) => quantity < tier.quantity);

    return tiers
      .map((tier) => {
        const isUnlocked = quantity >= tier.quantity;
        const isNext = nextTier && nextTier.quantity === tier.quantity;
        const position = (tier.quantity / maxQuantity) * 100;
        const classes = ['mix-match-progress__marker'];

        if (isUnlocked) classes.push('is-unlocked');
        if (isNext) classes.push('is-next');

        return `<li class="${classes.join(' ')}" style="left:${position}%">${this.#tierLabel(tier)}</li>`;
      })
      .join('');
  }

  #buildMessageHtml(tiers, quantity) {
    const unlockedTiers = tiers.filter((tier) => quantity >= tier.quantity);
    const currentTier = unlockedTiers[unlockedTiers.length - 1] || null;
    const nextTier = tiers.find((tier) => quantity < tier.quantity) || null;

    if (this.refs.progressHeadline instanceof HTMLElement) {
      if (currentTier) {
        this.refs.progressHeadline.textContent = this.#replaceTemplate(this.dataset.progressUnlockedTemplate, {
          reward: this.#tierLabel(currentTier),
          quantity: '0',
        });
      } else {
        this.refs.progressHeadline.textContent = this.#defaultProgressHeadline;
      }
    }

    if (!nextTier && currentTier) {
      return this.#replaceTemplate(this.dataset.progressCompleteTemplate, {
        reward: this.#tierLabel(currentTier),
        quantity: '0',
      });
    }

    if (!nextTier) return '';

    const remaining = Math.max(nextTier.quantity - quantity, 0);
    const raw = this.#replaceTemplate(this.dataset.progressRemainingTemplate, {
      reward: this.#tierLabel(nextTier),
      quantity: String(remaining),
    });

    return raw.replace(String(remaining), `<strong>${remaining}</strong>`);
  }

  #replaceTemplate(template, values) {
    if (!template) return '';

    let output = template;

    for (const [key, value] of Object.entries(values)) {
      output = output.replaceAll(`{{${key}}}`, value);
      output = output.replaceAll(`{{ ${key} }}`, value);
    }

    return output;
  }

  #getScopedQuantity() {
    const summary = this.querySelector('.mix-match-cart-summary[data-mix-match-summary]');
    if (!(summary instanceof HTMLElement)) return 0;

    const quantity = Number(summary.dataset.scopedQuantity || '0');
    return Number.isNaN(quantity) ? 0 : quantity;
  }

  #tierLabel(tier) {
    const value = Number(tier?.value);

    if (!Number.isFinite(value)) {
      return `${tier.quantity}`;
    }

    return `${value}%`;
  }

  #renderDiscountTiers(quantity) {
    const container = document.querySelector('[data-mix-match-discount-tiers]');
    if (!(container instanceof HTMLElement)) return;

    const inner = container.querySelector('.mix-match-discount-tiers__inner');
    if (!(inner instanceof HTMLElement)) return;

    const tiers = this.#getBundleTiers();
    if (tiers.length === 0) return;

    const displayTiers = tiers.slice(0, 3);

    let activeTierIndex = -1;
    for (let i = displayTiers.length - 1; i >= 0; i--) {
      if (quantity >= displayTiers[i].quantity) {
        activeTierIndex = i;
        break;
      }
    }

    const lastIndex = displayTiers.length - 1;
    const html = displayTiers
      .map((tier, index) => {
        const isActive = index === activeTierIndex;
        const isLast = index === lastIndex;
        const qtyLabel = isLast ? `${tier.quantity}+` : `${tier.quantity}`;
        const numericValue = Number.isFinite(tier.value) ? `${tier.value}` : qtyLabel;
        const buyGetLabel = this.#replaceTemplate(this.dataset.tierBuyGetTemplate || 'BUY {{quantity}} GET', { quantity: qtyLabel });
        const offLabel = this.dataset.tierOffLabel || 'OFF';
        return `<div class="mix-match-discount-tier${isActive ? ' is-active' : ''}">
          <span class="mix-match-discount-tier__label">${buyGetLabel}</span>
          <span class="mix-match-discount-tier__value">${numericValue}<span class="mix-match-discount-tier__percent">%<span class="mix-match-discount-tier__off">${offLabel}</span></span></span>
        </div>`;
      })
      .join('');

    inner.innerHTML = html;
  }

  /**
   * Read bundle tier data from the inline JSON rendered by Liquid.
   * @returns {Array<{quantity: number, value: number}>}
   */
  #getBundleTiers() {
    if (this.#cachedTiers) return this.#cachedTiers;

    try {
      const scriptId = this.dataset.tiersScriptId;
      if (!scriptId) return [];

      const scriptEl = document.getElementById(scriptId);
      if (!scriptEl) return [];

      const config = JSON.parse(scriptEl.textContent || '{}');

      if (config.status && config.status !== 'active') {
        const now = new Date();
        if (config.starts_at && new Date(config.starts_at) > now) return [];
        if (config.ends_at && new Date(config.ends_at) < now) return [];
        if (config.status !== 'scheduled') return [];
      }

      const rawTiers = Array.isArray(config.tiers) ? config.tiers : [];
      if (rawTiers.length === 0) return [];

      const tiers = rawTiers
        .map((tier) => {
          const quantity = Number(tier.minQuantity ?? tier.quantity ?? tier.min_quantity);
          const value = Number(tier.discountValue ?? tier.value ?? tier.discount_value);

          if (!Number.isFinite(quantity) || quantity <= 0) return null;

          return { quantity, value };
        })
        .filter(Boolean)
        .sort((a, b) => a.quantity - b.quantity);

      this.#cachedTiers = tiers;
      return tiers;
    } catch (error) {
      console.error('Failed to parse bundle tiers:', error);
      return [];
    }
  }

  #renderFallbackComponents() {
    const fallback = this.querySelector('[data-bundle-components-fallback]');
    if (!fallback) return;

    const tbody = fallback.querySelector('[data-bundle-fallback-body]');
    if (!tbody) return;

    const lineKey = fallback.dataset.bundleLineKey || '';
    const componentsRaw = fallback.dataset.componentsJson || '';

    let components = [];
    try {
      components = JSON.parse(componentsRaw);
      if (!Array.isArray(components)) components = [];
    } catch {
      components = [];
    }

    if (components.length === 0) {
      tbody.innerHTML = '';
      return;
    }

    const deleteIconSvg = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path class="remove-icon-bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/><path class="remove-icon-top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    const minusSvg = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/></svg>`;
    const plusSvg = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path class="vertical" d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/><path class="horizontal" d="M7 2.75L7 11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/></svg>`;
    const removeLabel = this.dataset.removeLabel || 'Remove';
    const decreaseLabel = this.dataset.decreaseQtyLabel || 'Decrease quantity';
    const increaseLabel = this.dataset.increaseQtyLabel || 'Increase quantity';

    const rows = [...components].reverse().map((comp) => {
      const info = this.#getProductInfoByVariantId(comp.variantId, comp.productId);
      const title = info.title || this.dataset.bundleItemFallbackTitle || '';
      const variantTitle = info.variantTitle || '';
      const imageUrl = info.imageUrl || '';

      const imageTd = imageUrl
        ? `<td class="cart-items__media swati-surface-muted" role="cell">
            <span class="cart-items__media-container" style="--ratio:1;">
              <img class="cart-items__media-image" src="${imageUrl}" alt="${this.#escapeHtml(title)}" width="250" loading="lazy" />
            </span>
          </td>`
        : `<td class="cart-items__media swati-surface-muted" role="cell"></td>`;

      const variantHtml = variantTitle && variantTitle !== 'Default Title'
        ? `<dl class="cart-items__variants"><div class="cart-items__variant"><dd>${this.#escapeHtml(variantTitle)}</dd></div></dl>`
        : '';

      return `<tr role="row" class="cart-items__table-row" data-key="${lineKey}" data-component-variant-id="${comp.variantId}">
        ${imageTd}
        <td class="cart-items__details cart-primary-typography" role="cell">
          <p><span class="cart-items__title">${this.#escapeHtml(title)}</span></p>
          ${variantHtml}
        </td>
        <td class="cart-items__quantity" role="cell">
          <div class="quantity-selector">
            <button
              class="button quantity-minus button-unstyled"
              type="button"
              data-bundle-change-qty
              data-bundle-line-key="${lineKey}"
              data-bundle-variant-id="${comp.variantId}"
              data-bundle-qty-delta="-1"
            >
              <span class="visually-hidden">${decreaseLabel}</span>
              <span class="svg-wrapper">${minusSvg}</span>
            </button>
            <input
              type="number"
              value="${comp.qty}"
              min="0"
              aria-label="${this.#escapeHtml(title)}"
              readonly
            >
            <button
              class="button quantity-plus button-unstyled"
              type="button"
              data-bundle-change-qty
              data-bundle-line-key="${lineKey}"
              data-bundle-variant-id="${comp.variantId}"
              data-bundle-qty-delta="1"
            >
              <span class="visually-hidden">${increaseLabel}</span>
              <span class="svg-wrapper">${plusSvg}</span>
            </button>
          </div>
        </td>
        <td class="cart-items__remove-row">
          <button class="button button--tertiary cart-items__remove" type="button" aria-label="${removeLabel} ${this.#escapeHtml(title)}" data-bundle-remove-component data-bundle-line-key="${lineKey}" data-bundle-variant-id="${comp.variantId}">
            ${deleteIconSvg}
            <span class="visually-hidden">${removeLabel}</span>
          </button>
        </td>
      </tr>`;
    });

    tbody.innerHTML = rows.join('');
  }

  /**
   * @param {number} variantId
   * @param {number} [productId]
   * @returns {{ title: string, variantTitle: string, imageUrl: string }}
   */
  #getProductInfoByVariantId(variantId, productId) {
    const optionEl = this.querySelector(
      `[data-mix-match-eye-select] option[data-variant-id="${variantId}"]`
    );

    if (optionEl) {
      const productCard = optionEl.closest('product-card');
      if (productCard) {
        return {
          title: this.#extractCardTitle(productCard),
          variantTitle: optionEl.value || '',
          imageUrl: this.#extractCardImage(productCard),
        };
      }
    }

    if (productId) {
      const productCard = this.querySelector(`product-card[data-product-id="${productId}"]`);
      if (productCard) {
        return {
          title: this.#extractCardTitle(productCard),
          variantTitle: optionEl?.value || '',
          imageUrl: this.#extractCardImage(productCard),
        };
      }

      const gridItem = this.querySelector(`li[data-product-id="${productId}"]`);
      const nestedCard = gridItem?.querySelector('product-card');
      if (nestedCard) {
        return {
          title: this.#extractCardTitle(nestedCard),
          variantTitle: '',
          imageUrl: this.#extractCardImage(nestedCard),
        };
      }
    }

    return { title: '', variantTitle: '', imageUrl: '' };
  }

  /**
   * @param {Element} productCard
   * @returns {string}
   */
  #extractCardTitle(productCard) {
    const hiddenTitle = productCard.querySelector('.product-card__link .visually-hidden');
    if (hiddenTitle) {
      const text = hiddenTitle.textContent?.trim();
      if (text) return text;
    }

    const titleLink = productCard.querySelector('[ref="productTitleLink"]');
    if (titleLink) {
      const text = titleLink.textContent?.trim();
      if (text) return text;
    }

    return '';
  }

  /**
   * @param {Element} productCard
   * @returns {string}
   */
  #extractCardImage(productCard) {
    const imgEl = productCard.querySelector('.card-gallery img, [ref="cardGallery"] img');
    return imgEl?.src || '';
  }

  /**
   * @param {string} str
   * @returns {string}
   */
  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * @param {string} lineKey
   * @param {number} variantId
   * @param {number} delta
   */
  async #updateComponentQty(lineKey, variantId, delta) {
    if (this.#isCartBusy) return;
    this.#isCartBusy = true;
    this.#setBundleLoading(variantId);

    const sectionsParam = this.#collectSectionIds();

    try {
      const cart = await this.#getCart();
      const bundleLine = cart.items?.find((item) => item.key === lineKey);
      if (!bundleLine) return;

      const components = this.#parseComponents(bundleLine);
      const target = components.find((c) => c.variantId === variantId);
      if (!target) return;

      target.qty = Math.max(0, target.qty + delta);

      const updatedComponents = components.filter((c) => c.qty > 0);

      if (updatedComponents.length === 0) {
        await this.#cartChange({
          id: lineKey,
          quantity: 0,
          sections: sectionsParam,
          sections_url: window.location.pathname + window.location.search,
        });
      } else {
        await this.#cartChange({
          id: lineKey,
          quantity: 1,
          properties: {
            ...bundleLine.properties,
            _components: JSON.stringify(updatedComponents),
          },
          sections: sectionsParam,
          sections_url: window.location.pathname + window.location.search,
        });
      }
    } catch (error) {
      if (!(/** @type {any} */ (error)?.swatiErrorDispatched)) {
        this.#dispatchCartError(this.dataset.cartUpdateError || 'Unable to update cart');
      }
      console.error('Failed to update component qty:', error);
    } finally {
      this.#clearBundleLoading();
      this.#isCartBusy = false;
    }
  }

  /**
   * @returns {Promise<any>} The full AJAX cart.
   */
  async #getCart() {
    return fetchCart();
  }

  #findExistingBundleLine(cart) {
    if (!Array.isArray(cart?.items)) return undefined;
    return cart.items.find((item) => item.properties?._bundle_type === 'mix-match');
  }

  #parseComponents(bundleLine) {
    try {
      const raw = bundleLine?.properties?._components;
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  #addOrIncrementComponent(components, variantId, productId) {
    const existing = components.find((c) => c.variantId === variantId);
    if (existing) {
      existing.qty += 1;
    } else {
      const entry = { variantId, qty: 1 };
      if (productId) entry.productId = productId;
      components.push(entry);
    }
  }

  /**
   * Our own section is left out: it keeps refreshing through the in-section cart-items
   * fallback render (and `#refreshOwnSection`), which uses the current URL and therefore
   * keeps the active filters.
   *
   * @returns {string} Comma separated section id list.
   */
  #collectSectionIds() {
    return cartSectionIds({ exclude: this });
  }

  /**
   * Performs one `/cart/change` mutation, dispatching a single `CartLinesUpdateEvent` for it.
   *
   * Throws on failure, because the callers' `catch` blocks own the error surface. When
   * `mutateCart` already dispatched a `CartErrorEvent` carrying Shopify's own message the
   * throw is marked so those blocks do not overwrite it with the generic one.
   *
   * @param {Record<string, any>} payload
   */
  async #cartChange(payload) {
    const body = JSON.stringify(payload);
    const quantity = Number(payload.quantity) || 0;

    const result = await mutateCart({
      source: this,
      action: quantity > 0 ? 'update' : 'remove',
      context: 'cart',
      lines: [{ id: payload.id, quantity }],
      detail: { source: 'mix-match-collection-component' },
      run: async () => {
        const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
        const responseText = await response.text();
        return responseText ? JSON.parse(responseText) : {};
      },
    });

    if (result.ok) return;

    const error = new Error(
      result.body?.errors || result.body?.description || result.error?.message || 'Cart update failed'
    );

    if (result.errorDispatched) {
      /** @type {any} */ (error).swatiErrorDispatched = true;
    }

    throw error;
  }

  async #removeComponentFromBundle(lineKey, variantIdToRemove) {
    if (this.#isCartBusy) return;
    this.#isCartBusy = true;
    this.#setBundleLoading(variantIdToRemove);

    const sectionsParam = this.#collectSectionIds();

    try {
      const cart = await this.#getCart();
      const bundleLine = cart.items?.find((item) => item.key === lineKey);
      if (!bundleLine) return;

      const components = this.#parseComponents(bundleLine);
      const updatedComponents = components.filter((c) => c.variantId !== variantIdToRemove);

      if (updatedComponents.length === 0) {
        await this.#cartChange({
          id: lineKey,
          quantity: 0,
          sections: sectionsParam,
          sections_url: window.location.pathname + window.location.search,
        });
      } else {
        await this.#cartChange({
          id: lineKey,
          quantity: 1,
          properties: {
            ...bundleLine.properties,
            _components: JSON.stringify(updatedComponents),
          },
          sections: sectionsParam,
          sections_url: window.location.pathname + window.location.search,
        });
      }
    } catch (error) {
      if (!(/** @type {any} */ (error)?.swatiErrorDispatched)) {
        this.#dispatchCartError(this.dataset.cartUpdateError || 'Unable to update cart');
      }
      console.error('Failed to remove bundle component:', error);
    } finally {
      this.#clearBundleLoading();
      this.#isCartBusy = false;
    }
  }

  #setBundleLoading(variantId) {
    const cartItems = this.querySelector('.mix-match-cart-items-component');
    if (cartItems instanceof HTMLElement) {
      cartItems.classList.add('cart-items-disabled');
    }

    const row = this.querySelector(`tr[data-component-variant-id="${variantId}"]`);
    if (row instanceof HTMLElement) {
      row.classList.add('mix-match-row-loading');
    }
  }

  #clearBundleLoading() {
    const cartItems = this.querySelector('.mix-match-cart-items-component');
    if (cartItems instanceof HTMLElement) {
      cartItems.classList.remove('cart-items-disabled');
    }

    for (const row of this.querySelectorAll('.mix-match-row-loading')) {
      row.classList.remove('mix-match-row-loading');
    }
  }

  /**
   * @param {Event} event
   */
  #handleBundleComponentAction = (event) => {
    if (!(event.target instanceof Element)) return;

    const checkoutBtn = event.target.closest('[data-mix-match-checkout]');
    if (checkoutBtn instanceof HTMLElement) {
      event.preventDefault();
      if (checkoutBtn.hasAttribute('aria-disabled')) return;
      const url = checkoutBtn.dataset.checkoutUrl || '/checkout';
      window.location.href = url;
      return;
    }

    const qtyBtn = event.target.closest('[data-bundle-change-qty]');
    if (qtyBtn instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();

      const lineKey = qtyBtn.dataset.bundleLineKey;
      const variantId = Number(qtyBtn.dataset.bundleVariantId);
      const delta = Number(qtyBtn.dataset.bundleQtyDelta);

      if (!lineKey || !variantId || !delta) return;
      this.#updateComponentQty(lineKey, variantId, delta);
      return;
    }

    const removeBtn = event.target.closest('[data-bundle-remove-component]');
    if (!(removeBtn instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const lineKey = removeBtn.dataset.bundleLineKey;
    const variantId = Number(removeBtn.dataset.bundleVariantId);

    if (!lineKey || !variantId) return;

    this.#removeComponentFromBundle(lineKey, variantId);
  };
}

if (!customElements.get('mix-match-collection-component')) {
  customElements.define('mix-match-collection-component', MixMatchCollectionComponent);
}
